// Modules
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, FormGroup, InputGroup, Intent } from '@blueprintjs/core'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

// Components
import Logo from 'components/brand/logo/Logo'
import Page from 'components/layout/page/Page'

// Hooks
import useFormControls from 'hooks/validation/useFormControls'
import useLanguage from 'hooks/system/useLanguage'
import useNotices from 'hooks/system/useNotices'

// Helpers
import { getAuthorizationHeader } from 'helpers/authentication'

// Constants
import { PASSWORD_CHANGE_URL } from 'constants/environment'

// Styles
import './Form.scss'

// Section: Password change > Landing > Form
function Form () {
	// References
	const form = useRef()

	// State
	const [formData, setFormData] = useState({ oldPassword: '', password: '', passwordConfirmation: '' })
	const [isInitialized, setIsInitialized] = useState(false)
	const [isValidating, setIsValidating] = useState(false)
	const [isFormReady, setIsFormReady] = useState(false)
	const [isProcessing, setIsProcessing] = useState(false)

	// Data
	const navigate = useNavigate()
	const { addNotice } = useNotices()
	const { translate } = useLanguage('pages.passwordChange.views.landing.sections.form')
	const {
		isValid: isPasswordValid,
		issues: passwordIssues
	} = useFormControls(['hasAtLeast8Characters', 'hasLowerCaseLetter', 'hasNumber', 'hasSpecialCharacter', 'hasUpperCaseLetter'], formData.password)
	const {
		isValid: isPasswordConfirmationValid,
		issues: passwordConfirmationIssues
	} = useFormControls(['hasMatchingValue'], formData.passwordConfirmation, formData.password)

	// Data (Memoized)
	const arePasswordsValid = useMemo(() => (isPasswordValid && isPasswordConfirmationValid), [isPasswordConfirmationValid, isPasswordValid])

	// Functions
	function handleInput (type, value) {
		setFormData(formData => {
			const newData = { ...formData, [type]: value }
			return newData
		})
	}
	async function handleSubmit () {
		setIsProcessing(true)
		try {
			const response = await axios({
				headers: { ...getAuthorizationHeader() },
				data: {
					oldPassword: formData?.oldPassword,
					password: formData?.password
				},
				method: 'POST',
				url: PASSWORD_CHANGE_URL
			})
			if (response.status < 300) {
				addNotice({ code: 'passwordChangeSuccess', type: 'success' })
				navigate('/')
			}
			setIsProcessing(false)
		} catch (error) {
			addNotice({ code: `server.${error?.response?.data?.message}`, log: error, type: 'error' })
			setIsProcessing(false)
		}
	}

	// Effects
	useEffect(() => { // TODO: Remove this after being sure that browser is not autofilling
		setTimeout(() => {
			setIsInitialized(true)
			const isFilled = (
				Array
					.from(form.current?.querySelectorAll('input'))
					?.every(input => input.matches(':-internal-autofill-selected'))
			)
			if (isFilled) setIsFormReady(true)
		}, 1000)
	}, [])
	useEffect(() => {
		setIsFormReady(arePasswordsValid)
	}, [arePasswordsValid])

	// Render
	return (
		<Page.Section
			willGrow
			willCenter
			name="form">
			<div className={
				'aix-authentication-form' +
				(isInitialized ? ' aix-initialized' : '')
			}>
				<Logo
					className="aix-logo"
					isCommonColor={!isInitialized} />
				<div className="aix-content">
					<form
						ref={form}
						className="aix-form aix-paper-thick">
						<h2 className="aix-title">{translate.local('form.title')}</h2>
						<FormGroup>
							<InputGroup
								name="old-password"
								type="password"
								value={formData.oldPassword}
								className="aix-input aix-password"
								placeholder={translate.local('form.fields.oldPassword.placeholder')}
								onChange={({ target }) => handleInput('oldPassword', target.value)} />
						</FormGroup>
						<FormGroup
							intent={Intent[isValidating && !isPasswordValid ? 'DANGER' : 'NONE']}
							helperText={
								isValidating && !isPasswordValid
									? translate.global(`notices.warning.form.${passwordIssues[0]}Warning`)
									: null
							}>
							<InputGroup
								name="new-password"
								type="password"
								value={formData.password}
								className="aix-input aix-password"
								intent={Intent[isValidating && !isPasswordValid ? 'DANGER' : 'NONE']}
								placeholder={translate.local('form.fields.password.placeholder')}
								onChange={({ target }) => handleInput('password', target.value)} />
						</FormGroup>
						<FormGroup
							intent={Intent[isValidating && !isPasswordConfirmationValid ? 'DANGER' : 'NONE']}
							helperText={
								isValidating && !isPasswordConfirmationValid
									? translate.global(`notices.warning.form.${passwordConfirmationIssues[0]}Warning`)
									: null
							}>
							<InputGroup
								name="password-confirmation"
								type="password"
								value={formData.passwordConfirmation}
								className="aix-input aix-password"
								intent={Intent[isValidating && !isPasswordConfirmationValid ? 'DANGER' : 'NONE']}
								placeholder={translate.local('form.fields.passwordConfirmation.placeholder')}
								onChange={({ target }) => handleInput('passwordConfirmation', target.value)} />
						</FormGroup>
						<Button
							fill
							disabled={!Object.values(formData).every(Boolean) || isProcessing || (isValidating && !isFormReady)}
							loading={isProcessing}
							className="aix-submit bp4-large"
							onClick={event => {
								event.preventDefault()
								setIsValidating(!isFormReady)
								if (isFormReady) handleSubmit()
							}}
							type="submit">
							{translate.local('form.actions.changePassword')}
						</Button>
					</form>
				</div>
			</div>
		</Page.Section>
	)
}

// Export
export default Form
